// Ops version of the crew app's rates sheet: same two-step upload, English
// only, on the crew's page. Every upload here is recorded as the admin's.
//
// The first request is always a dry run — ops reads what would change (and
// every problem) before a second, explicit click applies it. A file with
// errors never gets an Apply button at all.
import { useRef, useState } from 'react';
import { DetailSection } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import { ApiError } from '@/lib/api';
import { fetchVendorSheet, uploadVendorSheet, type VendorSheetReport } from '@/lib/admin-api';

async function encodeFile(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

const MAX_BYTES = 2 * 1024 * 1024;

export function VendorSheetSection({ vendorId }: { vendorId: number }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<{ base64: string; filename: string; report: VendorSheetReport } | null>(null);
  const [failed, setFailed] = useState<VendorSheetReport | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const reset = () => { setPending(null); setFailed(null); if (inputRef.current) inputRef.current.value = ''; };

  const download = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await fetchVendorSheet(vendorId);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'rates.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setNotice({ kind: 'error', text: 'Could not prepare the sheet.' });
    } finally {
      setBusy(false);
    }
  };

  const check = async (file: File) => {
    reset();
    setNotice(null);
    if (file.size > MAX_BYTES) {
      setNotice({ kind: 'error', text: 'That file is too large (2 MB limit).' });
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setBusy(true);
    try {
      const base64 = await encodeFile(file);
      const report = await uploadVendorSheet(vendorId, { file_base64: base64, filename: file.name, dry_run: true });
      setPending({ base64, filename: file.name, report });
    } catch (err) {
      if (isTooLarge(err)) {
        setNotice({ kind: 'error', text: 'That file is too large (2 MB limit).' });
        return;
      }
      const body = err instanceof ApiError ? (err.body as VendorSheetReport | undefined) : undefined;
      if (body && Array.isArray(body.errors)) setFailed(body);
      else setNotice({ kind: 'error', text: 'Could not read that file.' });
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const r = await uploadVendorSheet(vendorId, { file_base64: pending.base64, filename: pending.filename, dry_run: false });
      setNotice({
        kind: 'ok',
        text: `Applied: ${r.changes.prices} prices, ${r.changes.seasons} seasons, ${r.changes.tiers} multi-day bands, ${r.changes.blackouts} blackouts.`,
      });
      reset();
    } catch (err) {
      if (isTooLarge(err)) {
        setNotice({ kind: 'error', text: 'That file is too large (2 MB limit).' });
        return;
      }
      setNotice({ kind: 'error', text: 'Checked but could not be applied. Try again.' });
    } finally {
      setBusy(false);
    }
  };

  const line = (e: VendorSheetReport['errors'][number]) =>
    e.row != null ? `${e.sheet} row ${e.row}: ${e.message}` : `${e.sheet}: ${e.message}`;

  return (
    <DetailSection title="Rates sheet">
      <p className="mb-3 text-[12.5px] text-[color:var(--bo-mute)]">
        Download this crew&apos;s rates, seasons, multi-day bands and blackouts as one spreadsheet, or
        upload an edited one on their behalf. Uploads are checked first; for every car in the file, its
        seasons, bands and blackouts become exactly what the file says.
      </p>
      <div className="flex flex-wrap gap-2">
        <RowAction busy={busy} onClick={() => void download()}>Download sheet</RowAction>
        <label className="cursor-pointer rounded-md border border-[color:var(--bo-line)] px-2.5 py-1 text-[12px] font-medium">
          Upload rates sheet
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            aria-label="Upload rates sheet"
            className="sr-only"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void check(f);
            }}
          />
        </label>
      </div>
      {notice ? (
        <p
          role={notice.kind === 'error' ? 'alert' : 'status'}
          className={`mt-3 rounded-lg px-3 py-2 text-[12.5px] ${notice.kind === 'error' ? 'bg-[#fff1f2] text-[#9f1239]' : 'bg-[#f0fdf4] text-[#166534]'}`}
        >
          {notice.text}
        </p>
      ) : null}
      {failed ? (
        <div role="alert" className="mt-3 rounded-lg bg-[#fff1f2] p-3 text-[12.5px] text-[#9f1239]">
          <p className="font-medium">{failed.errors.length} problem(s) — nothing will be changed</p>
          <ul className="mt-2 list-disc space-y-1 ps-5">
            {failed.errors.map((e, i) => (
              <li key={i}>{line(e)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {pending ? (
        <div className="mt-3 rounded-lg bg-[color:var(--bo-bg)] p-3 text-[12.5px]">
          <p>
            {pending.report.cars_listed} cars listed · {pending.report.changes.prices} prices ·{' '}
            {pending.report.changes.seasons} seasons · {pending.report.changes.tiers} multi-day bands ·{' '}
            {pending.report.changes.blackouts} blackouts
          </p>
          <div className="mt-2 flex gap-2">
            <RowAction kind="primary" busy={busy} onClick={() => void apply()}>Apply</RowAction>
            <RowAction onClick={reset}>Cancel</RowAction>
          </div>
        </div>
      ) : null}
    </DetailSection>
  );
}

/** The server's 413: `{ error: 'file_too_large', max_bytes }`, no `errors` array. */
function isTooLarge(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 413) return true;
  const body = err.body as { error?: string } | undefined;
  return body?.error === 'file_too_large';
}
